<template>
  <li
    v-reveal
    class="row"
    :class="{ 'is-open': open }"
    :style="{ '--reveal-i': index }"
    role="button"
    tabindex="0"
    :aria-expanded="open ? 'true' : 'false'"
    :aria-controls="dossierId"
    :aria-label="ariaLabel"
    @click="toggle"
    @keydown.enter.prevent="toggle"
    @keydown.space.prevent="toggle"
  >
    <!-- Colour channel 1. A 3px tick at the row's true left edge, so the
         ledger at rest is ink on near-white with a column of colour down
         its side: a legend, not a rainbow. -->
    <span class="tick" :style="{ backgroundImage: tick }"></span>

    <span class="cell cell--mark">
      <ProjectMark :project="project" :size="markSize" />
    </span>

    <span class="cell cell--title">
      <span class="title">{{ title }}</span>
      <span v-if="!isNarrow" class="summary">{{ summary }}</span>
      <!-- The narrow grid has no year track, so the year rides with the
           title rather than leaving the row without its date. -->
      <span v-else class="num meta">{{ project.year }}</span>
    </span>

    <span v-if="!isNarrow" class="cell label cell--cat">{{ category }}</span>
    <span v-if="!isNarrow" class="cell num cell--year">{{ project.year }}</span>

    <span class="cell cell--chev">
      <svg
        class="chev"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    </span>
  </li>
</template>

<script>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import ProjectMark from "@/components/project/ProjectMark.vue";
import { gradientOf } from "@/composables/useProjectAssets";
import { useIsNarrow } from "@/composables/useMediaQuery";

export default {
  name: "LedgerRow",
  components: { ProjectMark },
  props: {
    project: { type: Object, required: true },
    index: { type: Number, default: 0 },
    open: { type: Boolean, default: false },
  },
  emits: ["toggle"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const isNarrow = useIsNarrow();

    const loc = (field) => {
      if (!field) return "";
      if (typeof field === "string") return field;
      return field[locale.value] || field.en || "";
    };

    const title = computed(() => loc(props.project.title));
    const summary = computed(() => loc(props.project.summary));
    const category = computed(() => loc(props.project.category));
    const tick = computed(() => gradientOf(props.project, 180));
    const dossierId = computed(() => `dossier-${props.project.id}`);
    const ariaLabel = computed(() => t("ledger.row.aria", { project: title.value }));
    const markSize = computed(() => (isNarrow.value ? 40 : 44));

    const toggle = () => emit("toggle", props.project.id);

    return {
      isNarrow,
      title,
      summary,
      category,
      tick,
      dossierId,
      ariaLabel,
      markSize,
      toggle,
    };
  },
};
</script>

<style scoped>
.row {
  position: relative;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 132px 84px 44px;
  align-items: center;
  gap: var(--s-5);
  min-height: 88px;
  padding: 20px 8px;
  border-top: var(--hair-w) solid var(--hairline);
  list-style: none;
  cursor: pointer;
  background-color: transparent;
}

/* The hover wash lives on a pseudo-element, not on the <li> itself.
   LedgerSection puts `.ledger-item { transition: opacity … }` on this same
   root node and `v-reveal` puts `.reveal { transition: opacity, transform … }`
   on it too — `transition` is a shorthand, so whichever of the three
   declarations wins silently deletes the other two. Owning a separate
   element means this row's 140 ms wash is never the casualty. */
.row::before {
  content: "";
  position: absolute;
  inset: 0;
  background-color: transparent;
  transition: background-color var(--d-1) linear;
  pointer-events: none;
}

/* Paints the cells above the wash without creating a stacking context.
   The tick is already positioned and follows ::before in the DOM, so it
   paints above it without help. */
.cell {
  position: relative;
}

.row:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}

.tick {
  position: absolute;
  left: 0;
  top: 50%;
  width: 3px;
  height: 44px;
  margin-top: -22px;
  border-radius: 1px;
  transition: width var(--d-2) var(--ease-out);
}

.cell {
  min-width: 0;
}

.cell--mark {
  display: flex;
  justify-content: flex-end;
}

.cell--title {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.title {
  font-size: var(--t-lead);
  font-weight: 600;
  letter-spacing: var(--ls-title);
  line-height: 1.25;
  color: var(--ink);
}

.summary {
  font-size: var(--t-sm);
  line-height: 1.5;
  color: var(--grey);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.meta {
  font-size: var(--m-value);
  color: var(--quiet);
}

.cell--cat,
.cell--year {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell--year {
  font-size: var(--m-value);
  font-weight: 500;
  color: var(--ink);
}

.cell--chev {
  display: flex;
  justify-content: center;
  color: var(--quiet);
}

.chev {
  transform: translateX(0) rotate(0deg);
  transition: transform var(--d-3) var(--ease-out);
}

.row.is-open .chev {
  transform: translateX(0) rotate(90deg);
}

/* Hover is CSS only — no listener, no rect read, no lift, no shadow.
   Gated on a real pointer so a tap does not leave the state stuck. */
@media (hover: hover) {
  .row:hover::before {
    background-color: rgba(180, 82, 45, 0.05);
  }
  .row:hover .tick {
    width: 5px;
  }
  .row:hover .chev {
    transform: translateX(3px) rotate(0deg);
  }
  .row.is-open:hover .chev {
    transform: translateX(3px) rotate(90deg);
  }
}

.row:active::before {
  background-color: rgba(180, 82, 45, 0.09);
}

@media (max-width: 720px) {
  .row {
    grid-template-columns: 40px minmax(0, 1fr) 40px;
    gap: var(--s-4);
    min-height: 64px;
    padding: 12px 8px;
  }
  .tick {
    height: 40px;
    margin-top: -20px;
  }
}
</style>
